import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, MoreHorizontal } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { SettingsSMS } from "@/routes/settings/SettingsSMS";

interface Campaign {
  _id: string;
  name: string;
  body: string;
  sendAt?: string;
  status?: string;
  workerStatus?: string;
  cost?: number;
}

function canOpenSmsCampaignEditor(campaign: Campaign) {
  return (
    campaign.status === "draft" ||
    (campaign.status === "queued" && campaign.workerStatus === "queued")
  );
}

function isSmsCampaignProcessing(campaign: Campaign) {
  return campaign.workerStatus === "processing";
}

function canOpenSmsCampaignMessages(campaign: Campaign) {
  return campaign.status === "active" || isSmsCampaignProcessing(campaign);
}

export function MessagesSMS() {
  const navigate = useNavigate();
  const location = useLocation();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await fetch("/api/campaigns");

        if (response.ok) {
          const data = await response.json();
          setCampaigns(data.campaigns || []);
        } else {
          console.error("Failed to fetch campaigns");
        }

        setBreadcrumbs([
          { label: "Messages", path: "/messages" },
          { label: "SMS", path: "/messages/sms" },
          { label: "Campaigns", path: "/messages/sms/campaigns" },
        ]);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();

    return () => {
      clearBreadcrumbs();
    };
  }, []);

  const handleCompose = async () => {
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const name = `Untitled ${year}-${month}-${day}`;

      const response = await fetch("/api/campaigns", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          body: "",
          status: "draft",
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create campaign");
      }

      const data = await response.json();
      navigate(`/messages/sms/campaigns/${data.campaign._id}`);
    } catch (error) {
      console.error("Error creating campaign:", error);
      alert(error instanceof Error ? error.message : "Failed to create campaign");
    }
  };

  const handleDelete = async (campaignId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!confirm("Are you sure you want to delete this SMS campaign?")) {
      return;
    }

    setDeletingId(campaignId);
    try {
      const response = await fetch(`/api/campaigns/${campaignId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setCampaigns(campaigns.filter((campaign) => campaign._id !== campaignId));
      } else {
        const error = await response.json();
        alert(error.error || "Failed to delete SMS campaign");
      }
    } catch (error) {
      console.error("Error deleting SMS campaign:", error);
      alert("An error occurred while deleting the SMS campaign");
    } finally {
      setDeletingId(null);
    }
  };

  const handleTabChange = (value: string) => {
    if (value === "inbox") {
      navigate("/messages/sms/inbox");
    } else if (value === "campaigns") {
      navigate("/messages/sms/campaigns");
    } else if (value === "settings") {
      navigate("/messages/sms/settings");
    }
  };

  const currentTab = location.pathname.includes("/inbox")
    ? "inbox"
    : location.pathname.includes("/settings")
      ? "settings"
      : "campaigns";

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <Tabs 
      value={currentTab}
      onValueChange={handleTabChange}
      className="w-full h-full flex flex-col"
    >
      <div className="p-4 border-b">
        <TabsList>
          <TabsTrigger value="inbox">Inbox</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="campaigns" className="m-0">
        <div className="p-4">
          <div className="flex justify-end mb-4">
            <Button onClick={handleCompose}>
              Compose
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns && campaigns.length > 0 ? (
                [...campaigns].sort((a, b) => b._id.localeCompare(a._id)).map((campaign) => (
                  <TableRow
                    key={campaign._id}
                    onClick={() => {
                      if (canOpenSmsCampaignEditor(campaign)) {
                        navigate(`/messages/sms/campaigns/${campaign._id}`);
                      } else if (canOpenSmsCampaignMessages(campaign)) {
                        navigate(`/messages/sms/campaigns/${campaign._id}/messages`);
                      }
                    }}
                    className={
                      canOpenSmsCampaignEditor(campaign) ||
                      canOpenSmsCampaignMessages(campaign)
                        ? "cursor-pointer hover:bg-muted/50"
                        : ""
                    }
                  >
                    <TableCell>
                      {isSmsCampaignProcessing(campaign) ? (
                        <Badge className="bg-sky-600 text-white gap-1.5 pr-2.5">
                          <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                          Sending
                        </Badge>
                      ) : (
                        <Badge
                          className={
                            campaign.status === "active"
                              ? "bg-green-500 text-white"
                              : campaign.status === "queued"
                                ? "bg-amber-500 text-white capitalize"
                                : "bg-gray-500 text-white capitalize"
                          }
                        >
                          {campaign.status === "active"
                            ? "Sent"
                            : campaign.status === "queued"
                              ? "Queued"
                              : campaign.status || ""}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{campaign.name}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={deletingId === campaign._id}
                          >
                            {deletingId === campaign._id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <MoreHorizontal className="h-4 w-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => handleDelete(campaign._id, e)}
                            disabled={
                              !canOpenSmsCampaignEditor(campaign) ||
                              deletingId === campaign._id
                            }
                            className="text-destructive focus:text-destructive"
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="h-24 text-center">
                    No campaigns found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </TabsContent>
    </Tabs>
  );
}

export function MessagesSMSSettings() {
  const navigate = useNavigate();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    setBreadcrumbs([
      { label: "Messages", path: "/messages" },
      { label: "SMS", path: "/messages/sms" },
      { label: "Settings", path: "/messages/sms/settings" },
    ]);

    return () => {
      clearBreadcrumbs();
    };
  }, []);

  const handleTabChange = (value: string) => {
    if (value === "inbox") {
      navigate("/messages/sms/inbox");
    } else if (value === "campaigns") {
      navigate("/messages/sms/campaigns");
    }
  };

  return (
    <Tabs
      value="settings"
      onValueChange={handleTabChange}
      className="w-full h-full flex flex-col"
    >
      <div className="p-4 border-b">
        <TabsList>
          <TabsTrigger value="inbox">Inbox</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="settings" className="m-0">
        <div className="p-4">
          <SettingsSMS />
        </div>
      </TabsContent>
    </Tabs>
  );
}
